import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import moment from 'moment';
import { DATE_FILTERS, DateFilterOptions, QuarterFilterOption, SEASON_FILTERS } from './constants';

export interface DateRangeFilter extends DateRange {
  i18nKey?: string;
}

export interface DateOption {
  key: DateFilterOptions;
  i18nKey: string;
  daysOffset?: number;
  monthsOffset?: number;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export interface QuarterOption {
  section: QuarterFilterOption;
  i18nKey: string;
  startMonth: number;
  endMonth: number;
}

export interface MomentDateRange {
  start: moment.Moment;
  end: moment.Moment;
}

// @dynamic
@Injectable({ providedIn: 'root' })
export class DateRangeSelectorService implements OnDestroy {
  private currentKey$$ = new BehaviorSubject<DateFilterOptions>(DateFilterOptions.ThirtyDay);
  private dateRangeFilter$$ = new BehaviorSubject<DateRangeFilter>(null);
  private compareDateRangeFilter$$ = new BehaviorSubject<DateRangeFilter>(null);
  private currKeySub: Subscription;
  private customDates: MomentDateRange;
  private compareDates: DateRange;
  private changeDatePickerType$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private currentCompareDate$$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private static calculateStartDate(end: moment.Moment, daysOffset: number, monthsOffset?: number): moment.Moment {
    const date = moment(end);
    if (monthsOffset && monthsOffset !== 0) {
      date.subtract(monthsOffset, 'months');
      return date;
    }
    date.subtract(daysOffset, 'days');
    return date;
  }

  private static getEntryFor(key: DateFilterOptions): DateOption {
    if (key === DateFilterOptions.Month || key === DateFilterOptions.Quarter) {
      return SEASON_FILTERS.find((entry) => key === entry.key);
    }
    return DATE_FILTERS.find((entry) => key === entry.key);
  }

  constructor() {
    this.currKeySub = this.currentKey$$.subscribe((key) => {
      this.updateFilter(key);
      this.updateCompareFilter(key);
    });
  }

  ngOnDestroy(): void {
    this.currKeySub.unsubscribe();
  }

  public getKey(): DateFilterOptions {
    return this.currentKey$$.getValue();
  }

  public setKey(newKey: DateFilterOptions): void {
    this.currentKey$$.next(newKey);
  }

  public getFilter(): Observable<DateRangeFilter> {
    return this.dateRangeFilter$$;
  }

  public getCompareFilter(): Observable<DateRangeFilter> {
    return this.compareDateRangeFilter$$;
  }

  public setAsCustom(customDates: MomentDateRange, compareDateRange?: DateRange): void {
    this.customDates = { ...this.customDates, ...customDates };
    this.compareDates = { ...this.compareDates, ...compareDateRange };
    this.setKey(DateFilterOptions.Custom);
  }

  public setAsMonthly(compareDateRange: DateRange): void {
    this.compareDates = { ...this.compareDates, ...compareDateRange };
    this.setKey(DateFilterOptions.Month);
  }

  public setAsQuarter(compareDateRange: DateRange): void {
    this.compareDates = { ...this.compareDates, ...compareDateRange };
    this.setKey(DateFilterOptions.Quarter);
  }

  public setDatePickerType(useComparableDatePicker: boolean): void {
    this.changeDatePickerType$$.next(useComparableDatePicker);
  }

  public getDatePickerType(): Observable<boolean> {
    return this.changeDatePickerType$$;
  }

  public updatePanelCompareDate(newKey: DateFilterOptions, startDate?: Date, endDate?: Date): void {
    this.updateCompareDate(newKey, startDate, endDate);
  }

  public getCompareDate(): Observable<string> {
    return this.currentCompareDate$$.asObservable();
  }

  private updateFilter(newKey: DateFilterOptions): void {
    const { i18nKey, daysOffset } = DateRangeSelectorService.getEntryFor(newKey);

    let start: moment.Moment;
    let end: moment.Moment;

    if (newKey === DateFilterOptions.Custom) {
      start = this.customDates.start;
      end = this.customDates.end;
    } else {
      end = moment.utc();
      start = DateRangeSelectorService.calculateStartDate(end, daysOffset);
    }

    const newFilter: DateRangeFilter = {
      i18nKey,
      start: moment(start).startOf('day').toDate(),
      end: moment(end).endOf('day').toDate(),
    };

    this.dateRangeFilter$$.next(newFilter);
  }

  private updateCompareFilter(newKey: DateFilterOptions): void {
    const { i18nKey, daysOffset, monthsOffset } = DateRangeSelectorService.getEntryFor(newKey);
    let start: moment.Moment;
    let end: moment.Moment;

    if (
      newKey === DateFilterOptions.Custom ||
      newKey === DateFilterOptions.Month ||
      newKey === DateFilterOptions.Quarter
    ) {
      start = moment(this.compareDates.start);
      end = moment(this.compareDates.end);
    } else {
      end = moment().startOf('day');
      start = DateRangeSelectorService.calculateStartDate(end, daysOffset - 1, monthsOffset - 1);
    }

    const newFilter: DateRangeFilter = {
      i18nKey,
      start: start.toDate(),
      end: end.toDate(),
    };

    this.compareDateRangeFilter$$.next(newFilter);
  }

  private updateCompareDate(newKey: DateFilterOptions, startDate?: Date, endDate?: Date): void {
    const { daysOffset, monthsOffset } = DateRangeSelectorService.getEntryFor(newKey);

    let start: moment.Moment;
    let end: moment.Moment;
    const transferStartDate = moment(startDate);
    const transferEndDate = moment(endDate);
    if (newKey === DateFilterOptions.Custom) {
      const diff = transferEndDate.diff(transferStartDate) / (1000 * 24 * 60 * 60);
      end = DateRangeSelectorService.calculateStartDate(transferStartDate.startOf('day'), 1, 0);
      start = DateRangeSelectorService.calculateStartDate(transferStartDate, diff + 1, monthsOffset + 1);
    } else if (newKey === DateFilterOptions.Month) {
      start = moment()
        .year(startDate.getFullYear())
        .month(startDate.getMonth() - 1)
        .startOf('month');
      end = moment()
        .year(startDate.getFullYear())
        .month(startDate.getMonth() - 1)
        .endOf('month');
    } else if (newKey === DateFilterOptions.Quarter) {
      let compareYear = startDate.getFullYear();
      let startMonth = startDate.getMonth();
      if (startDate.getMonth() === 1) {
        compareYear -= 1;
        startMonth = 13;
      }
      end = moment()
        .year(compareYear)
        .month(startMonth - 1)
        .endOf('month');
      start = moment()
        .year(compareYear)
        .month(end.month() - 2)
        .startOf('month');
    } else {
      end = DateRangeSelectorService.calculateStartDate(
        moment().subtract(1, 'day').startOf('day'),
        daysOffset - 1,
        monthsOffset - 1,
      );
      start = DateRangeSelectorService.calculateStartDate(end, daysOffset - 1, monthsOffset - 1);
    }
    const compareRange: DateRange = {
      start: start.toDate(),
      end: end.toDate(),
    };
    this.currentCompareDate$$.next(
      moment(compareRange.start).format('ll') + ' - ' + moment(compareRange.end).format('ll'),
    );
  }

  public updateStartDate(newKey: DateFilterOptions): Date {
    const { daysOffset, monthsOffset } = DateRangeSelectorService.getEntryFor(newKey);
    const startDate = DateRangeSelectorService.calculateStartDate(
      moment().startOf('day'),
      daysOffset - 1,
      monthsOffset,
    );
    return moment(startDate).startOf('day').toDate();
  }
}
